import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {CreateIssueDialogEntry} from '@github-ui/issue-create/CreateIssueDialogEntry'
import type {OnCreateProps} from '@github-ui/issue-create/Model'
import type {IssueCreateOptionConfig, ScopedField} from '@github-ui/issue-create/OptionConfig'
import type {IssueCreateValueTypes} from '@github-ui/issue-create/TemplateArgs'
import {VALUES} from '@github-ui/issue-create/values'
import {fetchRepository, fetchTopRepositories} from '@github-ui/item-picker/RepositoryPicker'
import {noop} from '@github-ui/noop'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {AppPayloadContext} from '@github-ui/react-core/use-app-payload'
import {clearSessionStorageByKeyPrefix} from '@github-ui/use-safe-storage/session-storage'
import {createContext, memo, use, useCallback, useMemo, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {MemexColumnDataType} from '../api/columns/contracts/memex-column'
import type {IAssignee, IssueType, Milestone} from '../api/common-contracts'
import {useCommands} from '../commands/hook'
import {getInitialState} from '../helpers/initial-state'
import {buildScopedFieldsFromActions} from '../helpers/issue-field-scoped-fields'
import {fetchJSONIslandData} from '../helpers/json-island'
import {ViewerPrivileges} from '../helpers/viewer-privileges'
import {useAddMemexItem} from '../hooks/use-add-memex-item'
import {useEnabledFeatures} from '../hooks/use-enabled-features'
import {repositoryWithOwnerFromString} from '../models/repository'
import {getStorageKey} from '../platform/get-storage-key'
import {useAllColumns} from '../state-providers/columns/use-all-columns'
import {useProjectDetails} from '../state-providers/memex/use-project-details'
import {useCreateMemexItem} from '../state-providers/memex-items/use-create-memex-item'
import {useRepositories} from '../state-providers/repositories/use-repositories'
import type {OmnibarItemAttrs} from './omnibar/types'
import useToasts from './toasts/use-toasts'

interface IssuePrefill {
  issueTitle?: string
  issueTypeName?: string
  milestoneTitle?: string
  enforcedSelectedProjectTitle?: string
  assignees?: Array<IAssignee>
  scopedFields?: Array<ScopedField>
  repo?: {
    name: string
    owner: string
  }
}

/**
 * @param prefill Data to prefill in the dialog.
 * @param newItemAttributes Attributes to apply to the item upon creation, passed from the omnibar.
 */
type StartIssueCreator = (
  prefill: IssuePrefill,
  newItemAttributes?: OmnibarItemAttrs,
  returnFocusRef?: React.RefObject<HTMLElement | null>,
  onClose?: (options?: {cancelled?: boolean}) => void,
) => Promise<void>

type IssueCreatorContextValue = {
  start: StartIssueCreator
  prefetch: (repo?: IssuePrefill['repo']) => Promise<void>
  isOpen: boolean
}

const IssueCreatorContext = createContext<IssueCreatorContextValue | null>(null)

export const useStartIssueCreator = () => use(IssueCreatorContext)

interface IssueCreatorProps {
  onClose: (options?: {cancelled?: boolean}) => void
  visible: boolean
  prefill?: IssuePrefill
  returnFocusRef?: React.RefObject<HTMLElement | null>
  newItemAttributes?: OmnibarItemAttrs
}

const storageKeyPrefix = getStorageKey('issue-creator')

const repoVariables = (name = '', owner = '') => ({
  owner,
  name,
  includeTemplates: true,
})

const variablesHaveRepo = (currentRepoVariables: {owner: string; name: string}) => {
  return (
    currentRepoVariables.name !== undefined &&
    currentRepoVariables.owner !== undefined &&
    currentRepoVariables.name !== '' &&
    currentRepoVariables.owner !== ''
  )
}

// Needs to be a separate component so it can be inside the RelayProvider, and not
// conditionally rendered so that the repos can cache
const IssueCreator = ({onClose, prefill, newItemAttributes, returnFocusRef}: IssueCreatorProps) => {
  const {addToast} = useToasts()
  const {projectOwner, themePreferences} = getInitialState()
  const {memex_default_issue_create_repository} = useEnabledFeatures()
  const {defaultIssueCreateRepository} = useProjectDetails()
  const enabledFeatures = useEnabledFeatures()

  const emojiSkinTonePreference = themePreferences?.preferred_emoji_skin_tone

  const {createMemexItem} = useCreateMemexItem()

  const addItemToView = useAddMemexItem({
    updateActions: (newItemAttributes && newItemAttributes.updateColumnActions) || undefined,
    previousItemId: newItemAttributes?.previousItemId,
    // Adding draft items is a noop because we only support creating non-draft issues here
    onAddDraftItem: useCallback(() => Promise.resolve(null), []),
    onAddItem: useCallback(
      (item, repositoryId, memexProjectColumnValues, localColumnValues, previousMemexProjectItemId) =>
        createMemexItem(
          {
            contentType: item.type,
            content: {id: item.id, repositoryId},
            memexProjectColumnValues,
            localColumnValues,
            previousMemexProjectItemId,
          },
          newItemAttributes?.groupId,
          newItemAttributes?.secondaryGroupId,
        ),
      [createMemexItem, newItemAttributes?.groupId, newItemAttributes?.secondaryGroupId],
    ),
    isCreatingSubIssue: newItemAttributes?.isCreatingSubIssue,
  })

  const onCreate = useCallback(
    async ({issue, createMore}: OnCreateProps) => {
      const {databaseId, repository} = issue

      if (!createMore) {
        onClose()
      }

      if (!databaseId || !repository.databaseId) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({message: 'Could not add the new issue to the project: ID is missing', type: 'error'})
        return
      }

      await addItemToView({
        type: 'Issue',
        id: databaseId,
        repositoryId: repository.databaseId,
        repositoryOwner: repository.owner.login,
      })
    },
    [onClose, addItemToView, addToast],
  )

  const sharedProps = {
    onCreateSuccess: onCreate,
    onCancel: () => onClose({cancelled: true}),
    navigate: noop, // Not needed as it's only for fullscreen
    isCreateDialogOpen: true, // This is already handled by the parent
    setIsCreateDialogOpen: noop,
  }
  let initialValues: IssueCreateValueTypes | undefined = undefined
  if (prefill?.issueTitle) {
    initialValues = {
      appendTitleToTemplate: prefill.issueTitle,
    }
  }

  const defaultRepository = useMemo(() => {
    if (
      memex_default_issue_create_repository &&
      defaultIssueCreateRepository?.name &&
      defaultIssueCreateRepository?.owner
    ) {
      return {name: defaultIssueCreateRepository.name, owner: defaultIssueCreateRepository.owner}
    }
    if (prefill?.repo && variablesHaveRepo(prefill.repo)) {
      return prefill.repo
    }
    return undefined
  }, [memex_default_issue_create_repository, defaultIssueCreateRepository, prefill?.repo])

  const parent = newItemAttributes?.updateColumnActions?.find(
    action => action.dataType === MemexColumnDataType.ParentIssue,
  )?.value

  const optionConfig: IssueCreateOptionConfig = {
    emojiSkinTonePreference,
    scopedOrganization: projectOwner?.login,
    scopedProjectTitle: prefill?.enforcedSelectedProjectTitle,
    scopedIssueType: prefill?.issueTypeName,
    scopedMilestone: prefill?.milestoneTitle,
    scopedAssignees: prefill?.assignees,
    scopedFields: prefill?.scopedFields,
    showFullScreenButton: false,
    issueCreateArguments: {
      repository: defaultRepository,
      initialValues,
      parentIssue: newItemAttributes?.isCreatingSubIssue && parent ? {id: parent.globalRelayId} : undefined,
      isCreatingSubIssueFromProject: newItemAttributes?.isCreatingSubIssue || false,
    },
  }

  const appPayloadContextValue = useMemo(() => {
    const appPayload = fetchJSONIslandData('app-payload') || {
      current_user_settings: {copilot_show_functionality: false},
    }
    return {...appPayload, enabled_features: enabledFeatures}
  }, [enabledFeatures])

  return (
    <AnalyticsProvider appName="memex" category="Memex Project" metadata={{}}>
      <AppPayloadContext value={appPayloadContextValue}>
        <CreateIssueDialogEntry {...sharedProps} optionConfig={optionConfig} returnFocusRef={returnFocusRef} />
      </AppPayloadContext>
    </AnalyticsProvider>
  )
}

export const IssueCreatorProvider = memo(function IssueCreatorProvider({children}: {children: React.ReactNode}) {
  const {hasWritePermissions} = ViewerPrivileges()
  const {title, defaultIssueCreateRepository} = useProjectDetails()
  const {memex_default_issue_create_repository} = useEnabledFeatures()
  const environment = useRelayEnvironment()
  const {allColumnsRef} = useAllColumns()

  const [creatorProps, setCreatorProps] = useState<IssueCreatorProps | undefined>()

  const {suggestRepositories} = useRepositories()

  const getFallbackRepo = useCallback(async () => {
    // If a default repository is configured, use it instead of fetching suggestions
    if (
      memex_default_issue_create_repository &&
      defaultIssueCreateRepository?.name &&
      defaultIssueCreateRepository?.owner
    ) {
      return {name: defaultIssueCreateRepository.name, owner: defaultIssueCreateRepository.owner}
    }

    const fallbackRepo = (await suggestRepositories()).at(0)?.nameWithOwner
    if (fallbackRepo) {
      return repositoryWithOwnerFromString(fallbackRepo)
    }
  }, [suggestRepositories, memex_default_issue_create_repository, defaultIssueCreateRepository])

  const startCreateIssue = useCallback<StartIssueCreator>(
    async ({repo, issueTitle}, newItemAttributes, returnFocusRef, onClose) => {
      if (!repo) {
        repo = await getFallbackRepo()
      }
      // ensure a clear session storage state when starting, since memex doesn't want to maintain old state
      // getSafeConfig overrides storageKeyPrefix with `${owner}-${name}` when a repository is present,
      // so we replicate that logic here to clear the correct keys, that is, until what the correct
      // fix for https://github.com/github/issues/issues/20241 is determined and implemented.
      const repoNwo = repo && `${repo.owner}-${repo.name}`
      const effectivePrefix = repoNwo || storageKeyPrefix
      clearSessionStorageByKeyPrefix(effectivePrefix)

      const handleClose = (options?: {cancelled?: boolean}) => {
        onClose?.(options)
        setCreatorProps(undefined)
      }

      const prefilledIssueType = newItemAttributes?.updateColumnActions?.find(
        ({dataType}) => dataType === MemexColumnDataType.IssueType,
      )?.value as IssueType | undefined

      const prefilledMilestone = newItemAttributes?.updateColumnActions?.find(
        ({dataType}) => dataType === MemexColumnDataType.Milestone,
      )?.value as Milestone | undefined

      const prefilledAssignees = newItemAttributes?.updateColumnActions?.find(
        ({dataType}) => dataType === MemexColumnDataType.Assignees,
      )?.value as Array<IAssignee> | undefined

      const prefilledScopedFields = buildScopedFieldsFromActions(
        allColumnsRef.current,
        newItemAttributes?.updateColumnActions,
      )

      setCreatorProps({
        visible: true,
        prefill: {
          issueTitle,
          repo,
          enforcedSelectedProjectTitle: title,
          issueTypeName: prefilledIssueType?.name,
          milestoneTitle: prefilledMilestone?.title,
          assignees: prefilledAssignees,
          scopedFields: prefilledScopedFields.length ? prefilledScopedFields : undefined,
        },
        newItemAttributes,
        onClose: handleClose,
        returnFocusRef,
      })
    },
    [allColumnsRef, getFallbackRepo, title],
  )

  const prefetchCreateIssue = useCallback(
    async (repo: IssuePrefill['repo'] | undefined) => {
      if (!repo) {
        repo = await getFallbackRepo()
      }
      await fetchTopRepositories(environment, {first: VALUES.repositoriesPreloadCount, hasIssuesEnabled: true})
      if (repo && variablesHaveRepo(repo)) {
        const vars = repoVariables(repo.name, repo.owner)
        await fetchRepository(environment, vars.owner, vars.name)
      }
    },
    [environment, getFallbackRepo],
  )

  // add to command palette
  useCommands(
    () => (hasWritePermissions ? ['i', 'Create issue', 'create_issue', () => startCreateIssue({})] : null),
    [startCreateIssue, hasWritePermissions],
  )

  const value = useMemo(
    () => ({
      start: startCreateIssue,
      prefetch: prefetchCreateIssue,
      isOpen: !!creatorProps,
    }),
    [prefetchCreateIssue, startCreateIssue, creatorProps],
  )

  // Without write permissions, context provider should not render so `useCreateIssue` will return null
  return hasWritePermissions ? (
    <>
      <IssueCreatorContext value={value}>{children}</IssueCreatorContext>
      {creatorProps && <IssueCreator {...creatorProps} />}
    </>
  ) : (
    <>{children}</>
  )
})
